import * as React from "react";
import { useHistory } from "react-router-dom";
import LoadingIndicator from "../../components/LoadingIndicator";
import {
  ShopBuilderEventsSection,
  ShopBuilderHeader,
  ShopBuilderProducts,
} from "../../components/ShopBuilder";
import ShopSettingsDialog from "../../components/ShopBuilder/ShopSettingsDialog";
import ShopImagesEditor from "../../components/ShopImagesEditor/ShopImagesEditor";
import ProductModal from "../../components/ProductComposer";
import { InfluencerEvent, SignType } from "../../domain";
import { useUser } from "../../globalState";
import {
  mutateDashboardInfluencer,
  useActiveInfluencerWithSetter,
  useInfluencer,
  useInfluencerEvents,
  useQuery,
  useSales,
} from "../../hooks";
import { Page } from "../../themeV2";
import { refreshShopSetupStatus } from "../../util/events";
import SetupWarning from "./SetupWarning";
import ShopOpenNotice from "./ShopOpenNotice";
import css from "./ShopEditor.module.css";
import { dashboardPath } from "../../domain/dashboardPaths";

const SETTINGS_PARAM = "settings";

const readSettingsRequested = (params) => params.has(SETTINGS_PARAM);

const dupeProductIdContext = React.createContext(null);
export function useDupeProductIdContext() {
  return React.useContext(dupeProductIdContext);
}
const livestreamWarningContext = React.createContext(null);
export function useLivestreamWarningContext() {
  return React.useContext(livestreamWarningContext);
}

const ShopEditor = () => {
  const history = useHistory();
  const currentUser = useUser();
  const unmounted = React.useRef(false);
  const activeInfluencerIdRef = React.useRef(null);

  const { activeInfluencer } = useActiveInfluencerWithSetter();

  const { salesCountMap } = useSales({
    route: activeInfluencer?.route,
    influencerId: activeInfluencer?.influencerId,
  });

  const { influencerEvents } = useInfluencerEvents(
    activeInfluencer?.influencerId,
  );

  const [editingImages, setEditingImages] = React.useState(null);
  const [editingSettings, setEditingSettings] = React.useState(false);
  // ?settings=1 lets other surfaces deep-link straight into shop settings,
  // which is otherwise only reachable by a button on this page.
  const settingsRequested = useQuery(readSettingsRequested);
  const [addingProduct, setAddingProduct] = React.useState(false);
  const [dupeProductId, setDupeProductId] = React.useState(null);

  const livestreamWarning = React.useMemo(() => {
    if (!activeInfluencer) return false;
    if (!activeInfluencer.products) return false;

    const hasLivestream = activeInfluencer.products.some(
      (product) =>
        product.signedType === SignType.livestream && product.isActive,
    );

    return (
      hasLivestream &&
      (!influencerEvents?.length ||
        influencerEvents.every(InfluencerEvent.isNonLiveSigning))
    );
  }, [activeInfluencer, influencerEvents]);

  React.useEffect(() => {
    if (activeInfluencer.hasNoShopEditor) {
      history.push(
        dashboardPath({ shop: activeInfluencer.route, page: "sales" }),
      );
      return;
    }
    document.title = `Streamily | Dashboard | ${activeInfluencer.name} | Shop Editor`;
    activeInfluencerIdRef.current = activeInfluencer.influencerId;
  }, [history, activeInfluencer]);

  React.useEffect(() => {
    mutateDashboardInfluencer(activeInfluencer?.route);
    return () => {
      unmounted.current = true;
    };
  }, []);

  const { refresh, isLoading } = useInfluencer({
    route: activeInfluencer.route,
    forShopBuilder: true,
  });

  const closeSettings = React.useCallback(() => {
    setEditingSettings(false);

    const params = new URLSearchParams(history.location.search);

    if (!params.has(SETTINGS_PARAM)) {
      return;
    }

    params.delete(SETTINGS_PARAM);

    const search = params.toString();

    history.replace(
      `${history.location.pathname}${search ? `?${search}` : ""}`,
    );
  }, [history]);

  const onClose = React.useCallback(
    (props) => {
      const {
        shouldRefreshInfluencer,
        shouldRefreshActiveInfluencer,
        noRefreshShopStatus,
      } = props || {};

      if (shouldRefreshInfluencer) {
        refresh();
      }

      if (shouldRefreshActiveInfluencer) {
        mutateDashboardInfluencer(activeInfluencer?.route);
      }

      if (
        !noRefreshShopStatus &&
        (shouldRefreshInfluencer || shouldRefreshActiveInfluencer)
      ) {
        refreshShopSetupStatus();
      }
    },
    [refresh, activeInfluencer?.route],
  );

  if (isLoading) {
    return (
      <div className={css.loadingContainer}>
        <LoadingIndicator />
      </div>
    );
  }

  if (activeInfluencer.hasNoShopEditor) return null;

  return (
    <livestreamWarningContext.Provider value={livestreamWarning}>
      <dupeProductIdContext.Provider
        value={{ dupeProductId, setDupeProductId }}
      >
        <Page.Root>
          <Page.Head title="Shop Builder" />

          <Page.Section>
            <SetupWarning influencer={activeInfluencer} />
          </Page.Section>

          {currentUser.isAdmin && (
            <ShopOpenNotice
              influencerId={activeInfluencer.influencerId}
              shopName={activeInfluencer.name}
            />
          )}

          <Page.Section>
            <ShopBuilderHeader
              route={activeInfluencer.route}
              onEditImages={setEditingImages}
              setEditingSettings={setEditingSettings}
            />
          </Page.Section>

          <Page.Section>
            <ShopBuilderEventsSection route={activeInfluencer.route} />
          </Page.Section>

          <Page.Section>
            <ShopBuilderProducts
              route={activeInfluencer.route}
              setAddingProduct={setAddingProduct}
              salesCountMap={salesCountMap}
              onClose={onClose}
            />
          </Page.Section>

          {activeInfluencer && addingProduct && (
            <ProductModal
              livestreamWarning={livestreamWarning}
              onClose={(props) => {
                setAddingProduct(false);
                onClose(props);
              }}
            />
          )}

          <ShopImagesEditor
            open={Boolean(editingImages)}
            initialSection={editingImages ?? "headshot"}
            influencerId={activeInfluencer.influencerId}
            isCollection={Boolean(activeInfluencer.isGroupShop)}
            onClose={() => {
              setEditingImages(null);
              onClose({
                shouldRefreshInfluencer: true,
                shouldRefreshActiveInfluencer: true,
              });
            }}
          />

          {(editingSettings || settingsRequested) && (
            <ShopSettingsDialog
              route={activeInfluencer.route}
              influencer={activeInfluencer.raw()}
              influencerId={activeInfluencer.influencerId}
              onClose={() => {
                closeSettings();
                refresh();
                mutateDashboardInfluencer(activeInfluencer?.route);
                refreshShopSetupStatus();
              }}
            />
          )}
        </Page.Root>
      </dupeProductIdContext.Provider>
    </livestreamWarningContext.Provider>
  );
};

export default ShopEditor;
